/**
 * CTRL_06 Plugin - Model Performance Monitoring
 * Enhanced UI for performance metrics selection and drift detection
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Progress,
  Alert,
  Statistic,
  Divider,
  Space,
  Badge,
} from 'antd';
import {
  DashboardOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  WarningOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors, spacing } from '../../../../theme/designTokens';

/**
 * Performance Metric Card
 */
function MetricCard({ metric, enabled, onToggle, currentValue, baseline }) {
  const metrics = {
    accuracy: {
      icon: '🎯',
      name: 'Accuracy',
      unit: '%',
      category: 'quality',
      description: 'Overall prediction accuracy',
    },
    precision: {
      icon: '🔬',
      name: 'Precision',
      unit: '%',
      category: 'quality',
      description: 'Positive prediction accuracy',
    },
    recall: {
      icon: '🔍',
      name: 'Recall',
      unit: '%',
      category: 'quality',
      description: 'True positive detection rate',
    },
    f1_score: {
      icon: '⚖️',
      name: 'F1 Score',
      unit: '',
      category: 'quality',
      description: 'Balanced precision-recall metric',
    },
    latency_ms: {
      icon: '⚡',
      name: 'Latency',
      unit: 'ms',
      category: 'performance',
      description: 'Response time per request',
    },
    throughput: {
      icon: '🚀',
      name: 'Throughput',
      unit: 'req/s',
      category: 'performance',
      description: 'Requests processed per second',
    },
    error_rate: {
      icon: '⚠️',
      name: 'Error Rate',
      unit: '%',
      category: 'reliability',
      description: 'Failed prediction percentage',
    },
  };

  const metricInfo = metrics[metric] || { icon: '📊', name: metric, unit: '', category: 'other', description: '' };

  const categoryColors = {
    quality: 'blue',
    performance: 'purple',
    reliability: 'orange',
    other: 'default',
  };

  // Calculate status based on drift
  const drift = currentValue && baseline ? ((currentValue - baseline) / baseline * 100).toFixed(1) : 0;
  const status = Math.abs(drift) > 5 ? 'degraded' : 'healthy';

  return (
    <Card
      size="small"
      hoverable
      onClick={onToggle}
      style={{
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: enabled ? colors.primary : '#E4E1E8',
        backgroundColor: enabled ? '#F4F1FA' : '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        height: '100%',
      }}
      bodyStyle={{ padding: spacing.md, minHeight: 110 }}
    >
      <Row align="middle" gutter={8} wrap={false}>
        <Col flex="none">
          <div style={{ fontSize: 32, lineHeight: 1 }}>{metricInfo.icon}</div>
        </Col>
        <Col flex="auto">
          <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            {metricInfo.name}
            {enabled && (
              <CheckCircleOutlined
                style={{ color: colors.status.pass, fontSize: 16 }}
              />
            )}
          </div>
          <div style={{
            fontSize: 11,
            color: 'rgba(23, 5, 43, 0.65)',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {metricInfo.description}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Tag color={categoryColors[metricInfo.category]} style={{ fontSize: 10, margin: 0, fontWeight: 600 }}>
              {metricInfo.category.toUpperCase()}
            </Tag>
            {currentValue && (
              <Tag color={status === 'healthy' ? 'green' : 'red'} style={{ fontSize: 10, margin: 0 }}>
                {currentValue}{metricInfo.unit}
              </Tag>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
}

/**
 * Performance Monitoring Stats
 */
function PerformanceStats({ config }) {
  const metrics = config?.required_metrics || [];
  const degradationThreshold = config?.degradation_threshold_pct || 5;
  const driftEnabled = config?.enable_drift_detection || false;
  const interval = config?.monitoring_interval || 'daily';

  return (
    <Row gutter={[spacing.md, spacing.md]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Metrics Monitored"
            value={metrics.length}
            suffix="/ 7"
            prefix={<DashboardOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Degradation Alert"
            value={degradationThreshold}
            suffix="%"
            prefix={<WarningOutlined style={{ color: colors.severity.high }} />}
            valueStyle={{ color: colors.severity.high }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Drift Detection"
            value={driftEnabled ? 'Enabled' : 'Disabled'}
            prefix={
              driftEnabled ? (
                <CheckCircleOutlined style={{ color: colors.status.pass }} />
              ) : (
                <WarningOutlined style={{ color: colors.severity.medium }} />
              )
            }
            valueStyle={{
              color: driftEnabled ? colors.status.pass : colors.severity.medium,
              fontSize: 20,
            }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Check Frequency"
            value={interval}
            prefix={<ThunderboltOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary, fontSize: 20, textTransform: 'capitalize' }}
          />
        </Card>
      </Col>
    </Row>
  );
}

/**
 * CTRL_06 Plugin Component
 */
function CTRL_06_Plugin({ projectId, controlCode }) {
  const [showDashboard, setShowDashboard] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    // In production, fetch actual config
    setConfig({
      enabled: false,
      required_metrics: ['accuracy', 'latency_ms'],
      degradation_threshold_pct: 5,
      monitoring_interval: 'daily',
      enable_drift_detection: true,
    });
  }, [projectId, controlCode]);

  const handleToggleMetric = (metric) => {
    setConfig((prev) => {
      const currentMetrics = prev.required_metrics || [];
      const isEnabled = currentMetrics.includes(metric);

      return {
        ...prev,
        required_metrics: isEnabled
          ? currentMetrics.filter((m) => m !== metric)
          : [...currentMetrics, metric],
      };
    });
  };

  // Mock current values for demo
  const mockMetrics = {
    accuracy: { current: 94.2, baseline: 95.1 },
    precision: { current: 91.8, baseline: 92.3 },
    recall: { current: 93.5, baseline: 94.0 },
    f1_score: { current: 0.92, baseline: 0.93 },
    latency_ms: { current: 45, baseline: 42 },
    throughput: { current: 1250, baseline: 1300 },
    error_rate: { current: 1.2, baseline: 0.9 },
  };

  return (
    <div>
      {showDashboard && config && (
        <>
          <Alert
            message="Model Performance Monitoring Dashboard"
            description="Visual interface for tracking model health metrics. Select metrics to monitor for performance degradation and drift detection."
            type="info"
            showIcon
            closable
            onClose={() => setShowDashboard(false)}
            style={{ marginBottom: spacing.lg }}
          />

          <Card
            title={
              <Space>
                <DashboardOutlined />
                Performance Overview
              </Space>
            }
            style={{ marginBottom: spacing.lg }}
          >
            <PerformanceStats config={config} />
          </Card>

          <Divider orientation="left">Performance Metrics</Divider>

          <Alert
            message="Select Metrics to Monitor"
            description="Click cards to toggle metrics. Selected metrics will be tracked continuously for degradation."
            type="success"
            showIcon
            style={{ marginBottom: spacing.md }}
          />

          <Row gutter={[spacing.md, spacing.md]} style={{ marginBottom: spacing.lg }}>
            {['accuracy', 'precision', 'recall', 'f1_score', 'latency_ms', 'throughput', 'error_rate'].map(
              (metric) => (
                <Col xs={24} sm={12} md={8} key={metric}>
                  <MetricCard
                    metric={metric}
                    enabled={config.required_metrics?.includes(metric)}
                    onToggle={() => handleToggleMetric(metric)}
                    currentValue={mockMetrics[metric]?.current}
                    baseline={mockMetrics[metric]?.baseline}
                  />
                </Col>
              )
            )}
          </Row>

          <Divider />
        </>
      )}

      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_06_Plugin;
